<template>
  <div class="app-bar-location-selector d-flex align-center">
    <v-select
      v-if="hasLocations"
      class="app-bar-location-select mr-4"
      :items="visibleLocations"
      item-title="display_name"
      item-value="id"
      :model-value="selectedLocationId"
      :loading="isLoading"
      density="compact"
      :label="$t('common.label.myLocation')"
      hide-details
      variant="outlined"
      color="primary"
      prepend-inner-icon="mdi-map-marker"
      @update:model-value="handleSelectionChange"
    />
    <v-progress-circular
      v-else-if="isLoading"
      class="mr-4"
      size="24"
      width="3"
      color="primary"
      indeterminate
    />
    <v-btn v-else variant="text" color="primary" class="text-none mr-4" @click="openLocationDialog">
      {{ $t("common.action.addLocation") }}
    </v-btn>
    <location-dialog
      ref="locationDialog"
      dialog-action="create"
      @created="handleLocationCreated"
      @updated="handleDialogUpdated"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useLocationStore } from "@/terminals/stores/locations";
import { useAccountStore } from "@/account/stores/account";
import { useAuthStore } from "@/auth/stores/auth";
import LocationDialog from "@/terminals/components/location-and-terminals/LocationDialog.vue";
import type { LocationSummary } from "@/terminals/types/locations";
import { useRouter } from "vue-router";

const locationStore = useLocationStore();
const accountStore = useAccountStore();
const authStore = useAuthStore();
const router = useRouter();
const { loading: isLoadingRef, selectedLocation } = storeToRefs(locationStore);
const { user } = storeToRefs(authStore);

const accountId = computed(() => accountStore.account?.id ?? null);
const locations = computed(() => locationStore.globalLocations?.locations ?? []);
const assignedLocationIds = computed(() => {
  const id = accountId.value;
  if (!id) {
    return null;
  }
  return user.value?.accounts?.[id]?.assignedLocationIds ?? null;
});
const visibleLocations = computed(() => {
  const allLocations = locations.value;
  const ids = assignedLocationIds.value;

  if (!Array.isArray(ids)) {
    return allLocations;
  }

  const allowedLocationIds = new Set(ids);
  return allLocations.filter((location) => allowedLocationIds.has(location.id));
});
const isLoading = computed(() => isLoadingRef.value);
const hasLocations = computed(() => visibleLocations.value.length > 0);
const selectedLocationId = computed(() => selectedLocation.value?.id ?? null);

const locationDialog = ref<InstanceType<typeof LocationDialog> | null>(null);

watch(
  accountId,
  (newAccount, previousAccount) => {
    if (previousAccount && previousAccount !== newAccount) {
      locationStore.stopLocationsListener();
    }
    if (newAccount) {
      locationStore.startLocationsListener(newAccount);
    }
  },
  { immediate: true },
);

const ensureSelectedLocationIsVisible = () => {
  const availableLocations = visibleLocations.value;

  if (!availableLocations.length) {
    if (selectedLocation.value) {
      locationStore.setSelectedLocation(null);
    }
    return;
  }

  const currentId = selectedLocation.value?.id;
  const isCurrentVisible = currentId
    ? availableLocations.some((location) => location.id === currentId)
    : false;

  if (isCurrentVisible) {
    return;
  }

  const nextLocation = availableLocations[0];
  if (nextLocation && nextLocation.id !== currentId) {
    locationStore.setSelectedLocation(nextLocation);
  }
};

watch(
  [visibleLocations, assignedLocationIds, selectedLocationId],
  ensureSelectedLocationIsVisible,
  {
    immediate: true,
  },
);

const handleSelectionChange = (locationId: string | null) => {
  if (!locationId) {
    locationStore.setSelectedLocation(null);
    return;
  }

  const nextLocation = visibleLocations.value.find((location) => location.id === locationId);

  if (nextLocation) {
    locationStore.setSelectedLocation(nextLocation);
  }
};

const openLocationDialog = () => {
  locationDialog.value?.open();
};

const handleLocationCreated = (location: LocationSummary | null) => {
  if (!location?.id) {
    return;
  }

  locationStore.setSelectedLocation(location);
  router.push({ name: "locationDetail", params: { locationId: location.id } });
};

const handleDialogUpdated = () => {
  if (accountId.value) {
    locationStore.startLocationsListener(accountId.value);
  }
};

onBeforeUnmount(() => {
  locationStore.stopLocationsListener();
});
</script>

<style scoped>
.app-bar-location-select {
  min-width: 220px;
}
</style>
